import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import QueryBox from '../components/QueryBox'
import ResponsePanel, { ResponseData, FollowUpKey, RefinementAxis, Refinements } from '../components/ResponsePanel'
import SourceToggle from '../components/SourceToggle'
import HCPGate from '../components/HCPGate'
import { initPostHog, trackEvent, trackSessionStart, trackQuery } from '../lib/posthog'
import { supabase } from '../lib/supabase'

type Mode = 'clinic' | 'study'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.gpmeta.au'

// Map raw backend error responses to user-facing notice + kind.
// "Rate limit check failed" is the generic 500 from rateLimit middleware
// when the Supabase query itself errors (column missing, network, etc.) —
// users should never see that string. 429 with RATE_LIMIT_EXCEEDED is the
// real daily cap and its message is already user-friendly.
function mapApiError(
  status: number,
  errorCode?: string,
  message?: string
): { kind: NonNullable<ResponseData['error']>; message: string } {
  if (status === 401 || status === 403 || errorCode === 'UNAUTHORIZED') {
    return { kind: 'unauthenticated', message: 'Sign-in expired. Please sign in again.' }
  }
  if (status === 402) {
    return { kind: 'expired', message: message || 'Your subscription needs attention.' }
  }
  if (status === 429 && errorCode === 'RATE_LIMIT_EXCEEDED') {
    return { kind: 'cap_reached', message: message || 'Daily limit reached. Resets at midnight.' }
  }
  // RATE_LIMIT_ERROR (500) and any other 5xx — backend message is internal,
  // do not surface it.
  return { kind: 'system', message: 'Service temporarily unavailable. Please try again in a moment.' }
}

interface BackendQueryResponse {
  answer: string
  intent: string
  follow_ups: FollowUpKey[]
  refinement_axes?: RefinementAxis[]
  needs_clarification: boolean
  clarification_options?: string[]
  primary_source: string
  sources: { id: string; name: string; url?: string }[]
  citations?: { source: string; title?: string; url?: string }[]
  handout_links?: string[]
}

function Sidebar({
  isCollapsed,
  onToggle,
  userSourcePrefs,
  userLocation,
  onSourcePrefsChange
}: {
  isCollapsed: boolean
  onToggle: () => void
  userSourcePrefs: any
  userLocation: any
  onSourcePrefsChange: (prefs: any) => void
}) {
  const totalEnabled =
    userSourcePrefs.national.length +
    userSourcePrefs.state.length +
    userSourcePrefs.local.length +
    userSourcePrefs.specialty.length

  return (
    <div className={`bg-white border-r border-gray-200 transition-all duration-200 ${isCollapsed ? 'w-16' : 'w-80'}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-6">
          {!isCollapsed ? (
            <div>
              <h2 className="font-semibold text-gray-900">Sources</h2>
              <p className="text-xs text-gray-500">{totalEnabled} sources active</p>
            </div>
          ) : (
            <div className="text-center text-sm text-gray-600">
              <div className="font-medium">{totalEnabled}</div>
              <div className="text-xs">sources</div>
            </div>
          )}
          <button
            onClick={onToggle}
            className="p-2 hover:bg-gray-100 rounded-md"
          >
            <svg className={`w-5 h-5 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>

        <SourceToggle
          userPrefs={userSourcePrefs}
          userLocation={userLocation}
          onPrefsChange={onSourcePrefsChange}
          isCollapsed={isCollapsed}
        />
      </div>
    </div>
  )
}

function TopBar({
  mode,
  onModeChange,
  user,
  onSignOut
}: {
  mode: Mode
  onModeChange: (mode: Mode) => void
  user: any
  onSignOut: () => void
}) {
  const [showUserMenu, setShowUserMenu] = useState(false)

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-8">
          <h1 className="text-2xl font-bold text-blue-600">GPMeta</h1>

          {/* Mode Toggle — Clinic / Study only */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => onModeChange('clinic')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                mode === 'clinic' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Clinic
            </button>
            <button
              onClick={() => onModeChange('study')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                mode === 'study' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Study
            </button>
          </div>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-medium">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <span className="text-gray-700">{user?.name || user?.email}</span>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
              <a
                href="/settings"
                onClick={() => setShowUserMenu(false)}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Settings
              </a>
              <button
                onClick={() => {
                  setShowUserMenu(false)
                  onSignOut()
                }}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function AppMain() {
  const { user, signOut } = useAuth()
  const [mode, setMode] = useState<Mode>('clinic')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isQueryLoading, setIsQueryLoading] = useState(false)
  const [response, setResponse] = useState<ResponseData | undefined>(undefined)
  const [lastQuery, setLastQuery] = useState<string>('')
  // Refinements scope the active answer along axes returned by the model
  // (severity, site, age band, etc.). Reset on each fresh query.
  const [activeRefinements, setActiveRefinements] = useState<Refinements>({})
  const [userSourcePrefs, setUserSourcePrefs] = useState({
    national: ['etg', 'racgp', 'pbs', 'mbs', 'amh', 'nps', 'healthdirect', 'pubmed', 'ashm'],
    state: ['sa_health'],
    local: [],
    specialty: []
  })
  const [userLocation] = useState({ state: 'SA', lhn: 'CALHN' })
  const [showHCPGate, setShowHCPGate] = useState(true)

  useEffect(() => {
    initPostHog()
  }, [])

  useEffect(() => {
    if (user && !showHCPGate) {
      const totalSources =
        userSourcePrefs.national.length +
        userSourcePrefs.state.length +
        userSourcePrefs.local.length +
        userSourcePrefs.specialty.length
      trackSessionStart(mode, totalSources, isMobile, user.id)
    }
  }, [user, showHCPGate, mode, userSourcePrefs, isMobile])

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true)
  }, [isMobile])

  const runQuery = async (
    query: string,
    intent?: FollowUpKey,
    refinements?: Refinements,
  ) => {
    setIsQueryLoading(true)
    setResponse(undefined)
    setLastQuery(query)
    const startTime = Date.now()
    const requestRefinements = refinements ?? {}

    const totalSources =
      userSourcePrefs.national.length +
      userSourcePrefs.state.length +
      userSourcePrefs.local.length +
      userSourcePrefs.specialty.length

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        setResponse({
          answer: 'Sign-in required.',
          error: 'unauthenticated',
          citations: [],
          followUps: [],
          queryId: `err_${Date.now()}`,
        })
        return
      }

      const res = await fetch(`${API_URL}/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          query,
          mode,
          intent: intent ?? undefined,
          refinements: Object.keys(requestRefinements).length > 0 ? requestRefinements : undefined,
        }),
      })

      const responseTimeMs = Date.now() - startTime

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({})) as { message?: string; error?: string }
        const friendly = mapApiError(res.status, errBody.error, errBody.message)
        setResponse({
          answer: friendly.message,
          error: friendly.kind,
          citations: [],
          followUps: [],
          queryId: `err_${Date.now()}`,
        })
        return
      }

      const data: BackendQueryResponse = await res.json()

      trackQuery({
        mode,
        queryLength: query.length,
        sourceCount: totalSources,
        responseTimeMs,
        userId: user?.id,
      })

      setResponse({
        answer: data.answer,
        primarySource: data.primary_source,
        followUps: data.follow_ups ?? [],
        refinementAxes: data.refinement_axes ?? [],
        needsClarification: data.needs_clarification ?? false,
        clarificationOptions: data.clarification_options ?? [],
        citations: (data.citations ?? data.sources ?? []).map(c => ({
          source: 'name' in c ? c.name : c.source,
          title: 'title' in c ? c.title : undefined,
          url: c.url,
        })),
        queryId: `query_${Date.now()}`,
      })
      setActiveRefinements(requestRefinements)
    } catch (error) {
      console.error('Query error:', error)
      setResponse({
        answer: 'Network error. Check your connection and try again.',
        error: 'system',
        citations: [],
        followUps: [],
        queryId: `err_${Date.now()}`,
      })
    } finally {
      setIsQueryLoading(false)
    }
  }

  const handleQuerySubmit = (query: string) => {
    // New query → clear all refinements (per design: reset each query).
    setActiveRefinements({})
    runQuery(query, undefined, {})
  }

  // Re-issue the original query scoped to a follow-up intent.
  // The backend uses `intent` to scope the answer, so the query text
  // does not need a "— more on X" suffix. Follow-up = new line of enquiry,
  // so refinements reset.
  const handleFollowUp = (key: FollowUpKey) => {
    if (!lastQuery) return
    setActiveRefinements({})
    runQuery(lastQuery, key, {})
  }

  // Add a refinement (e.g. severity=mild) and re-issue the same query.
  // The model gets ACTIVE REFINEMENTS in the prompt and returns an answer
  // scoped to that combination, plus any finer axes that remain material.
  const handleRefine = (axisKey: string, option: string) => {
    if (!lastQuery) return
    const next = { ...activeRefinements, [axisKey]: option }
    runQuery(lastQuery, undefined, next)
  }

  const handleClearRefinement = (axisKey: string) => {
    if (!lastQuery) return
    const next = { ...activeRefinements }
    delete next[axisKey]
    runQuery(lastQuery, undefined, next)
  }

  // Clarifier chip click → re-issue query with the option appended.
  // Treat as a fresh line of enquiry: refinements reset.
  const handleClarify = (option: string) => {
    if (!lastQuery) return
    setActiveRefinements({})
    runQuery(`${lastQuery} ${option}`, undefined, {})
  }

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const handleFeedback = async (queryId: string, feedback: 'up' | 'down', text?: string) => {
    trackEvent('feedback_submitted', {
      user_id: user?.id,
      query_id: queryId,
      feedback,
      has_text: !!text,
      mode
    })
  }

  const handleSourcePrefsChange = (newPrefs: typeof userSourcePrefs) => {
    const oldTotal = Object.values(userSourcePrefs).flat().length
    const newTotal = Object.values(newPrefs).flat().length
    if (oldTotal !== newTotal) {
      trackEvent('source_toggled', {
        user_id: user?.id,
        source_id: 'multiple',
        source_tier: 'national',
        enabled: newTotal > oldTotal,
        total_sources_enabled: newTotal
      })
    }
    setUserSourcePrefs(newPrefs)
  }

  const handleHCPGateAccept = () => {
    setShowHCPGate(false)
  }

  const handleModeChange = (newMode: Mode) => {
    if (newMode === mode) return
    const oldMode = mode
    setMode(newMode)
    trackEvent('mode_switched', {
      user_id: user?.id,
      from_mode: oldMode,
      to_mode: newMode,
      trigger: 'manual'
    })
  }

  if (showHCPGate) {
    return <HCPGate onAccept={handleHCPGateAccept} userId={user?.id} />
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <TopBar
        mode={mode}
        onModeChange={handleModeChange}
        user={user}
        onSignOut={handleSignOut}
      />

      <div className="flex flex-1">
        {!(isMobile && mode === 'clinic') && (
          <Sidebar
            isCollapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
            userSourcePrefs={userSourcePrefs}
            userLocation={userLocation}
            onSourcePrefsChange={handleSourcePrefsChange}
          />
        )}

        <div className="flex-1 p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <QueryBox onSubmit={handleQuerySubmit} isLoading={isQueryLoading} />
            </div>

            <ResponsePanel
              response={response}
              isLoading={isQueryLoading}
              mode={mode}
              activeRefinements={activeRefinements}
              onFeedback={handleFeedback}
              onFollowUp={handleFollowUp}
              onRefine={handleRefine}
              onClearRefinement={handleClearRefinement}
              onClarify={handleClarify}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
